/**
 * Affordability utility - Single source of truth for cost checking
 *
 * Usage:
 *   import { canAfford } from '../modules/shared/affordability';
 *   if (canAfford(cost, playerResources)) { ... }
 */

/**
 * Check if the player can afford a cost object
 * @param {Object} cost - Object mapping resource IDs to amounts needed
 * @param {Object} resources - Object mapping resource IDs to amounts owned
 * @returns {boolean} True if player has enough of every resource
 */
export function canAfford(cost, resources) {
  if (!cost || !resources) return false;

  return Object.entries(cost).every(([resourceId, amount]) => {
    const owned = resources[resourceId] || 0;
    return owned >= amount;
  });
}

/**
 * Get missing resources for a cost
 * @param {Object} cost - Object mapping resource IDs to amounts needed
 * @param {Object} resources - Object mapping resource IDs to amounts owned
 * @returns {Object} Object mapping resource IDs to amounts still needed (only missing ones)
 */
export function getMissingResources(cost, resources) {
  if (!cost || !resources) return cost || {};

  const missing = {};
  Object.entries(cost).forEach(([resourceId, amount]) => {
    const owned = resources[resourceId] || 0;
    if (owned < amount) {
      missing[resourceId] = amount - owned;
    }
  });
  return missing;
}

/**
 * Deduct cost from resources (returns new object, does not mutate)
 * @param {Object} cost - Object mapping resource IDs to amounts to deduct
 * @param {Object} resources - Object mapping resource IDs to amounts owned
 * @returns {Object} New resources object with costs deducted
 */
export function deductCost(cost, resources) {
  if (!cost || !resources) return resources;

  const newResources = { ...resources };
  Object.entries(cost).forEach(([resourceId, amount]) => {
    newResources[resourceId] = (newResources[resourceId] || 0) - amount;
  });
  return newResources;
}
